/**
 * LineupsWidget — Organized starting lineup display with deduplication
 * - Fetches from #starting-lineups channel
 * - Deduplicates by game matchup (keeps latest update)
 * - Groups by game date, today first
 * - Collapsible game cards with 2-column lineup layout
 * Refresh: 60s | CSS prefix: luw-
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchFromProxy, imageUrl } from '../lib/api'
import { findTeam } from '../lib/teamLogos'
import { RefreshCw, ChevronDown, ChevronRight, Users, AlertCircle, Image } from 'lucide-react'
import './LineupsWidget.css'

/** Parse "Tue Mar 24" style date from description like "Tue Mar 24 . 8:10 PM ET" */
function parseDateFromDesc(desc) {
  if (!desc) return null
  const clean = desc.replace(/[^\w\s:./]/g, ' ').trim()
  // Match patterns like "Tue Mar 24 . 8:10 PM ET" or "Tue Mar 24 8:10 PM ET"
  const m = clean.match(/\w{3}\s+(\w{3})\s+(\d{1,2})/)
  if (!m) return null
  const monthStr = m[1]
  const day = parseInt(m[2], 10)
  const months = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 }
  const month = months[monthStr]
  if (month === undefined) return null
  const now = new Date()
  let year = now.getFullYear()
  // Handle year boundary
  if (month < now.getMonth() - 6) year++
  return new Date(year, month, day)
}

/** Parse game time from description */
function parseTimeFromDesc(desc) {
  if (!desc) return null
  const clean = desc.replace(/[^\w\s:./]/g, ' ').trim()
  const m = clean.match(/(\d{1,2}:\d{2}\s*(?:AM|PM)\s*ET)/i)
  return m ? m[1].trim() : null
}

/** Extract away/home team abbrs from field names like "Away . LAA" or "Home . LAD" */
function parseTeamFromFieldName(name) {
  if (!name) return null
  // Pattern: "Away – LAA" or "Home – LAD" (with various dash types)
  const m = name.match(/(Away|Home)\s*[–\-—]\s*(\w+)/i)
  if (m) return { side: m[1].toLowerCase(), abbr: m[2].trim() }
  return null
}

/** Parse matchup from title like "Los Angeles Angels @ Los Angeles Dodgers (Updated)" */
function parseMatchupFromTitle(title) {
  if (!title) return null
  // Remove emojis and (Updated) etc
  const clean = title
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '')
    .replace(/\(.*?\)/g, '')
    .replace(/\*\*/g, '')
    .replace(/\s+[—–-]\s+.*$/g, '')
    .trim()
  const m = clean.match(/(.+?)\s*[@]\s*(.+)/)
  if (!m) return null
  return { awayText: m[1].trim(), homeText: m[2].trim() }
}

/** Create a dedup key from matchup */
function matchupKey(awayText, homeText) {
  const normalize = (s) => s.toLowerCase().replace(/[^a-z]/g, '')
  return `${normalize(awayText)}@${normalize(homeText)}`
}

/** Parse lineup from field value string */
function parseLineup(value) {
  if (!value) return []
  return value.split('\n').filter(line => line.trim()).map(line => {
    const m = line.match(/^(\d+)\.\s*(.+?)\s*\((\w+)\)\s*$/)
    if (m) return { order: parseInt(m[1], 10), name: m[2].trim(), position: m[3] }
    // Fallback: just the line
    const m2 = line.match(/^(\d+)\.\s*(.+)/)
    if (m2) return { order: parseInt(m2[1], 10), name: m2[2].trim(), position: '' }
    return { order: 0, name: line.trim(), position: '' }
  }).filter(p => p.name)
}

function formatDateLabel(date) {
  if (!date) return 'Unknown Date'
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  const diff = (d - today) / 86400000
  if (diff === 0) return 'Today'
  if (diff === 1) return 'Tomorrow'
  if (diff === -1) return 'Yesterday'
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
}

export default function LineupsWidget() {
  const [games, setGames] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expanded, setExpanded] = useState({})
  const [lastUpdated, setLastUpdated] = useState(null)

  const fetchData = useCallback(async () => {
    const { data, status, message } = await fetchFromProxy('/posts', { channel: '#starting-lineups', limit: '100' })
    if (status !== 'ok' || !data) {
      setError(message || 'Failed to load lineups')
      setLoading(false)
      return
    }

    const posts = Array.isArray(data) ? data : (data.posts || [])

    // Process BOTH text_embed and graphic posts
    const gameMap = new Map() // matchupKey -> game object

    for (const post of posts) {
      // Parse matchup from title (text_embed) or content (graphic)
      const titleText = post.title || post.content || ''
      const matchup = parseMatchupFromTitle(titleText)
      if (!matchup) continue

      // Filter out non-MLB teams (preseason/exhibition games)
      const awayCheck = findTeam(matchup.awayText)
      const homeCheck = findTeam(matchup.homeText)
      if (!awayCheck || !homeCheck) continue

      const key = matchupKey(matchup.awayText, matchup.homeText)
      const existing = gameMap.get(key)

      if (post.post_type === 'graphic' && post.image_url) {
        // Graphic post — attach image to existing game or create stub
        if (existing) {
          // Only update image if this graphic is newer
          if (!existing.imageUrl || new Date(post.timestamp) > new Date(existing.imageTimestamp || 0)) {
            existing.imageUrl = imageUrl(post.image_url)
            existing.imageTimestamp = post.timestamp
          }
        } else {
          // Create entry with just the image — text_embed may come later
          const dateObj = parseDateFromDesc(post.content)
          const gameTime = parseTimeFromDesc(post.content)
          const awayTeam = findTeam(matchup.awayText) || { name: matchup.awayText, abbr: '', logo: null }
          const homeTeam = findTeam(matchup.homeText) || { name: matchup.homeText, abbr: '', logo: null }
          gameMap.set(key, {
            key, awayTeam, homeTeam, awayLineup: [], homeLineup: [],
            gameTime, dateObj, isUpdate: titleText.includes('Updated'),
            timestamp: post.timestamp, title: titleText,
            imageUrl: imageUrl(post.image_url), imageTimestamp: post.timestamp,
          })
        }
        continue
      }

      if (post.post_type !== 'text_embed' || !post.fields?.length) continue

      // Keep only the latest text_embed per matchup
      if (existing?.awayLineup?.length > 0 && new Date(existing.timestamp) > new Date(post.timestamp)) continue

      const dateObj = parseDateFromDesc(post.description)
      const gameTime = parseTimeFromDesc(post.description)
      const isUpdate = post.title?.includes('Updated')

      let awayTeam = null
      let homeTeam = null
      let awayLineup = []
      let homeLineup = []

      for (const field of post.fields) {
        const parsed = parseTeamFromFieldName(field.name)
        if (parsed) {
          const team = findTeam(parsed.abbr)
          if (parsed.side === 'away') {
            awayTeam = team || { abbr: parsed.abbr, name: matchup.awayText, logo: null }
            awayLineup = parseLineup(field.value)
          } else {
            homeTeam = team || { abbr: parsed.abbr, name: matchup.homeText, logo: null }
            homeLineup = parseLineup(field.value)
          }
        }
      }

      // Fallback team lookup from title
      if (!awayTeam) awayTeam = findTeam(matchup.awayText) || { name: matchup.awayText, abbr: '', logo: null }
      if (!homeTeam) homeTeam = findTeam(matchup.homeText) || { name: matchup.homeText, abbr: '', logo: null }

      // Preserve existing image if we're updating text data
      const prevImage = existing?.imageUrl || null
      const prevImageTs = existing?.imageTimestamp || null

      gameMap.set(key, {
        key, awayTeam, homeTeam, awayLineup, homeLineup,
        gameTime, dateObj, isUpdate, timestamp: post.timestamp, title: post.title,
        imageUrl: prevImage, imageTimestamp: prevImageTs,
      })
    }

    // Group by date
    const grouped = {}
    for (const game of gameMap.values()) {
      const dateKey = game.dateObj ? game.dateObj.toISOString().slice(0, 10) : 'unknown'
      if (!grouped[dateKey]) grouped[dateKey] = []
      grouped[dateKey].push(game)
    }

    // Sort dates: today first, then future, then past
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const todayStr = today.toISOString().slice(0, 10)

    const sortedDates = Object.keys(grouped).sort((a, b) => {
      if (a === todayStr) return -1
      if (b === todayStr) return 1
      return new Date(a) - new Date(b)
    })

    const result = sortedDates.map(dateKey => ({
      dateKey,
      dateLabel: formatDateLabel(dateKey === 'unknown' ? null : new Date(dateKey + 'T12:00:00')),
      games: grouped[dateKey].sort((a, b) => {
        // Sort by game time within date
        if (!a.gameTime && !b.gameTime) return 0
        if (!a.gameTime) return 1
        if (!b.gameTime) return -1
        return a.gameTime.localeCompare(b.gameTime)
      }),
    }))

    setGames(result)
    setError(null)
    setLoading(false)
    setLastUpdated(new Date())

    // Auto-expand today's games
    if (Object.keys(expanded).length === 0) {
      const autoExpand = {}
      for (const group of result) {
        if (group.dateKey === todayStr) {
          for (const g of group.games) autoExpand[g.key] = true
        }
      }
      setExpanded(autoExpand)
    }
  }, [])

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, 60000)
    return () => clearInterval(interval)
  }, [fetchData])

  const toggleGame = (key) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const totalGames = games.reduce((sum, g) => sum + g.games.length, 0)

  return (
    <div className="luw-widget">
      <div className="luw-header">
        <div className="luw-header-left">
          <Users size={18} className="luw-header-icon" />
          <h2 className="luw-title">Starting Lineups</h2>
          <span className="luw-count">{totalGames} game{totalGames !== 1 ? 's' : ''}</span>
        </div>
        <div className="luw-header-right">
          {lastUpdated && (
            <span className="luw-last-updated">
              {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button className="luw-refresh" onClick={fetchData} title="Refresh">
            <RefreshCw size={14} className={loading ? 'luw-spin' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div className="luw-error">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      {loading && !games.length ? (
        <div className="luw-loading">Loading lineups...</div>
      ) : (
        <div className="luw-body">
          {games.length === 0 && !loading && (
            <div className="luw-empty">No lineup data available</div>
          )}

          {games.map(group => (
            <div key={group.dateKey} className="luw-date-group">
              <div className="luw-date-header">
                <span className="luw-date-label">{group.dateLabel}</span>
                <span className="luw-date-count">{group.games.length} game{group.games.length !== 1 ? 's' : ''}</span>
              </div>

              {group.games.map(game => (
                <div key={game.key} className={`luw-game-card ${expanded[game.key] ? 'luw-expanded' : ''}`}>
                  <button className="luw-game-header" onClick={() => toggleGame(game.key)}>
                    <div className="luw-matchup-row">
                      <div className="luw-team-info">
                        {game.awayTeam?.logo && <img src={game.awayTeam.logo} alt="" className="luw-team-logo" />}
                        <span className="luw-team-name">{game.awayTeam?.short || game.awayTeam?.abbr || game.awayTeam?.name || '???'}</span>
                      </div>
                      <span className="luw-at">@</span>
                      <div className="luw-team-info">
                        {game.homeTeam?.logo && <img src={game.homeTeam.logo} alt="" className="luw-team-logo" />}
                        <span className="luw-team-name">{game.homeTeam?.short || game.homeTeam?.abbr || game.homeTeam?.name || '???'}</span>
                      </div>
                    </div>
                    <div className="luw-game-meta">
                      {game.gameTime && <span className="luw-game-time">{game.gameTime}</span>}
                      {game.isUpdate && <span className="luw-updated-badge">UPDATED</span>}
                      {expanded[game.key] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </div>
                  </button>

                  {expanded[game.key] && (
                    <div className="luw-expanded-body">
                      {/* Lineup card image from Discord */}
                      {game.imageUrl && (
                        <div className="luw-image-container">
                          <img
                            src={game.imageUrl}
                            alt="Lineup card"
                            className="luw-lineup-image"
                            loading="lazy"
                            onError={(e) => { e.target.style.display = 'none' }}
                          />
                        </div>
                      )}

                      {/* Structured lineup data */}
                      {(game.awayLineup.length > 0 || game.homeLineup.length > 0) && (
                        <div className="luw-lineups">
                          <div className="luw-lineup-col">
                            <div className="luw-lineup-header">
                              {game.awayTeam?.logo && <img src={game.awayTeam.logo} alt="" className="luw-lineup-logo" />}
                              <span>Away — {game.awayTeam?.abbr || game.awayTeam?.short || ''}</span>
                            </div>
                            <div className="luw-lineup-list">
                              {game.awayLineup.length > 0 ? game.awayLineup.map((p, i) => (
                                <div key={i} className="luw-player">
                                  <span className="luw-player-order">{p.order}.</span>
                                  <span className="luw-player-name">{p.name}</span>
                                  {p.position && <span className="luw-player-pos">{p.position}</span>}
                                </div>
                              )) : (
                                <div className="luw-no-lineup">No lineup posted</div>
                              )}
                            </div>
                          </div>

                          <div className="luw-lineup-divider" />

                          <div className="luw-lineup-col">
                            <div className="luw-lineup-header">
                              {game.homeTeam?.logo && <img src={game.homeTeam.logo} alt="" className="luw-lineup-logo" />}
                              <span>Home — {game.homeTeam?.abbr || game.homeTeam?.short || ''}</span>
                            </div>
                            <div className="luw-lineup-list">
                              {game.homeLineup.length > 0 ? game.homeLineup.map((p, i) => (
                                <div key={i} className="luw-player">
                                  <span className="luw-player-order">{p.order}.</span>
                                  <span className="luw-player-name">{p.name}</span>
                                  {p.position && <span className="luw-player-pos">{p.position}</span>}
                                </div>
                              )) : (
                                <div className="luw-no-lineup">No lineup posted</div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
